import { Form, Row, Col, Button, Modal } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { getParameterTypes } from '../../../../../../../redux/slices/lists/parameterTypesSlice';
import { LoadingSelect } from 'components/forms/LoadingFields';
import moment from 'moment-timezone';
import 'react-datetime/css/react-datetime.css';
import MaskedDateTimeField from 'components/forms/MaskedDateTimeField';

const toOptionsArray = (s = '') =>
  s
    ? s
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

const toKeyValueArray = (s = '') =>
  s
    ? s
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
        .map((pair) => {
          const [key, ...rest] = pair.split('=');
          return { key: (key ?? '').trim(), label: rest.join('=').trim() };
        })
        .filter(({ key, label }) => key && label)
    : [];

const normalizeKV = (s = '') => {
  const i = s.indexOf('=');
  return i >= 0 ? s.slice(0, i).trim() : s.trim();
};

const sortParameters = (arr = []) =>
  [...arr].sort((a, b) => {
    const ao = Number.isFinite(Number(a?.order)) ? Number(a.order) : 999999;
    const bo = Number.isFinite(Number(b?.order)) ? Number(b.order) : 999999;
    if (ao !== bo) return ao - bo;
    return String(a?.name || '').localeCompare(String(b?.name || ''));
  });

const parseDateTimeZoneValue = (raw: any) => {
  const str = String(raw ?? '').trim();
  if (!str) return { datetime: '', timezone: moment.tz.guess() };

  // format: YYYY-MM-DDTHH:mm:ss Europe/Amsterdam
  const spaceIndex = str.lastIndexOf(' ');
  if (spaceIndex > 0) {
    const dt = str.slice(0, spaceIndex).trim();
    const tz = str.slice(spaceIndex + 1).trim();
    return { datetime: dt, timezone: tz || moment.tz.guess() };
  }

  return { datetime: str, timezone: moment.tz.guess() };
};

const Parameters = ({ state, setState, onChange, disabled = false }) => {
  const dispatch = useAppDispatch();
  const { parameters } = state;

  const { parameter_types, status: parameterTypesStatus } = useAppSelector((state) => state?.listOfParameterTypes);

  const isParameterTypesLoading = parameterTypesStatus === 'loading';

  const [paramTimezones, setParamTimezones] = useState<Record<string, string>>({});

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsParameterIndex, setDetailsParameterIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (parameter_types.length) return;

      try {
        await dispatch(getParameterTypes());
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, []);

  const createNewParameter = () => {
    setState({
      ...state,
      parameters: [
        ...parameters,
        {
          name: '',
          parameter_type: 'String',
          default_expression: '',
          output_format: '',
          inout: 'inout',
          runtime: false,
          mandatory: false,
          options: '',
          order: (parameters?.length || 0) + 1,
          description: '',
        },
      ],
    });
  };

  const deleteParameter = (name) => {
    const currentParameterIndex = parameters.map((parameter) => parameter.name).indexOf(name);
    const mappedParameters = [...parameters];
    mappedParameters.splice(currentParameterIndex, 1);
    setState({
      ...state,
      parameters: mappedParameters.map((p, i) => ({
        ...p,
        order: i + 1,
      })),
    });
  };

  const sortedParameters = sortParameters(parameters || []);

  const moveParameter = (fromIndex: number, toIndex: number) => {
    if (disabled) return;
    if (toIndex < 0 || toIndex >= sortedParameters.length) return;

    const next = [...sortedParameters];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    setState({
      ...state,
      parameters: next.map((p, i) => ({
        ...p,
        order: i + 1,
      })),
    });
  };

  const emitDefaultExpressionChange = (idx: number, value: string) => {
    onChange({ target: { name: 'default_expression', value } }, idx);
  };

  const emitOutputFormatChange = (idx: number, value: string) => {
    onChange({ target: { name: 'output_format', value } }, idx);
  };

  const openDetails = (idx: number) => {
    setDetailsParameterIndex(idx);
    setShowDetailsModal(true);
  };

  const closeDetails = () => {
    setShowDetailsModal(false);
    setDetailsParameterIndex(null);
  };

  const detailsParameter = detailsParameterIndex !== null ? parameters?.[detailsParameterIndex] : null;

  return (
    <>
      {sortedParameters.map((item, index) => {
        const {
          name,
          parameter_type = 'String',
          inout = 'inout',
          default_expression,
          output_format = '',
          runtime = false,
          mandatory = false,
          options = '',
          description = '',
        } = item;

        const normalizedType = String(parameter_type).replace(/\s+/g, '').toLowerCase();

        const isDropdown = normalizedType === 'dropdown';
        const isKeyValueDropdown = normalizedType === 'keyvaluedropdown';

        const isDate = normalizedType === 'date';
        const isDateTime = normalizedType === 'datetime';
        const isDateTimeZone = normalizedType === 'datetimezone';
        const isTime = normalizedType === 'time';

        const optionList = toOptionsArray(options);
        const kvOptionList = toKeyValueArray(options);

        const originalIndex = parameters.indexOf(item);

        const datePickerValue = (() => {
          const s = String(default_expression ?? '').trim();
          if (!s) return '';
          const m = moment(s, 'YYYY-MM-DD', true);
          return m.isValid() ? m : '';
        })();

        const dateTimePickerValue = (() => {
          const s = String(default_expression ?? '').trim();
          if (!s) return '';
          const m = moment(s, moment.ISO_8601, true);
          return m.isValid() ? m : '';
        })();

        const dateTimeZoneParts = (() => {
          const parsed = parseDateTimeZoneValue(default_expression);
          const tzFromState = paramTimezones[name] || parsed.timezone || moment.tz.guess();

          if (!parsed.datetime) {
            return { momentValue: '', tzValue: tzFromState };
          }

          const m = moment(parsed.datetime, moment.ISO_8601, true);
          return { momentValue: m.isValid() ? m : '', tzValue: tzFromState };
        })();

        const timeMoment = (() => {
          const s = String(default_expression ?? '').trim();
          if (!s) return '';
          const m = moment(s, 'HH:mm:ss', true);
          return m.isValid() ? m : '';
        })();

        const showClear = (isDate || isDateTime || isDateTimeZone || isTime) && String(default_expression ?? '').trim().length > 0;

        const dateKey = `date-${originalIndex}`;
        const dateTimeKey = `dt-${originalIndex}`;
        const dateTimeZoneKey = `dtz-${originalIndex}`;

        return (
          <Row key={originalIndex}>
            <Col lg="auto">
              <Form.Group className="mb-2">
                <Form.Label>Order</Form.Label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button
                    variant="secondary"
                    disabled={disabled || index <= 0}
                    onClick={() => moveParameter(index, index - 1)}
                    style={{ padding: '2px 8px' }}
                    aria-label="move-parameter-up"
                  >
                    ↑
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={disabled || index >= sortedParameters.length - 1}
                    onClick={() => moveParameter(index, index + 1)}
                    style={{ padding: '2px 8px' }}
                    aria-label="move-parameter-down"
                  >
                    ↓
                  </Button>
                </div>
              </Form.Group>
            </Col>

            <Col lg="2">
              <Form.Group className="mb-2">
                <Form.Label>Name</Form.Label>
                <Form.Control aria-label="parameter-name" disabled={disabled} name="name" value={name ?? ''} onChange={(e) => onChange(e, originalIndex)} />
              </Form.Group>
            </Col>

            <Col lg="2">
              <Form.Group className="mb-2">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  aria-label="parameter-description"
                  disabled={disabled}
                  name="description"
                  value={description}
                  placeholder="User-friendly description"
                  onChange={(e) => onChange(e, originalIndex)}
                />
              </Form.Group>
            </Col>

            <Col lg="auto">
              <Form.Group className="mb-2">
                <Form.Label>Type</Form.Label>
                <LoadingSelect
                  aria-label="parameter-type-select"
                  disabled={disabled}
                  name="parameter_type"
                  value={parameter_type ?? ''}
                  onChange={(e) => onChange(e, originalIndex)}
                  isLoading={isParameterTypesLoading}
                  options={(parameter_types || []).map((parameter: any) => {
                    const { parameter_type } = parameter;
                    return {
                      value: parameter_type,
                      label: parameter_type,
                    };
                  })}
                  placeholder="Select type"
                  loadingLabel="Loading parameter types..."
                />
              </Form.Group>
            </Col>

            <Col lg="auto">
              <Form.Group>
                <Form.Label>In/Out</Form.Label>
                <Form.Select aria-label="parameter-inout-select" disabled={disabled} name="inout" value={inout} onChange={(e) => onChange(e, originalIndex)}>
                  <option value="in">In</option>
                  <option value="out">Out</option>
                  <option value="inout">Inout</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {(isDropdown || isKeyValueDropdown) && (
              <Col lg="4">
                <Form.Group>
                  <Form.Label>Options (comma-separated)</Form.Label>
                  <Form.Control
                    aria-label="options"
                    placeholder={isKeyValueDropdown ? 'e.g. 01=Test, 02=Test2' : 'e.g. val1, val2, val3'}
                    disabled={disabled}
                    name="options"
                    value={options ?? ''}
                    onChange={(e) => onChange(e, originalIndex)}
                  />
                </Form.Group>
              </Col>
            )}

            <Col lg="3">
              <Form.Group>
                <Form.Label>Default Expression</Form.Label>
                {isDropdown && (
                  <Form.Select
                    aria-label="default_expression"
                    disabled={disabled || optionList.length === 0}
                    name="default_expression"
                    value={optionList.includes(default_expression) ? default_expression : ''}
                    onChange={(e) => onChange(e, originalIndex)}
                  >
                    {optionList.length === 0 ? (
                      <option value="">Add options above…</option>
                    ) : (
                      <>
                        <option value=""></option>
                        {optionList.map((opt, i) => (
                          <option key={i} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </>
                    )}
                  </Form.Select>
                )}

                {isKeyValueDropdown && (
                  <Form.Select
                    aria-label="default_expression"
                    disabled={disabled || kvOptionList.length === 0}
                    name="default_expression"
                    value={kvOptionList.some((o) => o.key === normalizeKV(default_expression || '')) ? normalizeKV(default_expression || '') : ''}
                    onChange={(e) => onChange(e, originalIndex)}
                  >
                    {kvOptionList.length === 0 ? (
                      <option value="">Add options above…</option>
                    ) : (
                      <>
                        <option value=""></option>
                        {kvOptionList.map(({ key, label }, i) => (
                          <option key={i} value={key}>
                            {label} ({key})
                          </option>
                        ))}
                      </>
                    )}
                  </Form.Select>
                )}

                {isDate && (
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ flex: 1 }}>
                      <MaskedDateTimeField
                        value={datePickerValue as any}
                        onChange={(dt) => {
                          if (disabled) return;
                          emitDefaultExpressionChange(originalIndex, dt.clone().seconds(0).milliseconds(0).format('YYYY-MM-DD'));
                        }}
                        mode="date"
                        placeholder="YYYY-MM-DD"
                        disabled={disabled}
                      />
                    </div>

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={disabled}
                      onClick={() => openDetails(originalIndex)}
                      aria-label="parameter-details"
                      title="Details"
                      style={{ padding: '0px 8px', height: 32 }}
                    >
                      Output Format
                    </Button>

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={disabled || !showClear}
                      onClick={() => emitDefaultExpressionChange(originalIndex, '')}
                    >
                      Clear
                    </Button>
                  </div>
                )}

                {isDateTime && (
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ flex: 1 }}>
                      <MaskedDateTimeField
                        value={dateTimePickerValue as any}
                        onChange={(dt) => {
                          if (disabled) return;
                          emitDefaultExpressionChange(originalIndex, dt.clone().milliseconds(0).format('YYYY-MM-DDTHH:mm:ss'));
                        }}
                        mode="datetime"
                        placeholder="YYYY-MM-DD HH:mm:ss"
                        disabled={disabled}
                      />
                    </div>

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={disabled}
                      onClick={() => openDetails(originalIndex)}
                      aria-label="parameter-details"
                      title="Details"
                      style={{ padding: '0px 8px', height: 32 }}
                    >
                      Output Format
                    </Button>

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={disabled || !showClear}
                      onClick={() => emitDefaultExpressionChange(originalIndex, '')}
                    >
                      Clear
                    </Button>
                  </div>
                )}

                {isDateTimeZone && (
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ flex: 1 }}>
                        <MaskedDateTimeField
                          value={dateTimeZoneParts.momentValue as any}
                          onChange={(dt) => {
                            if (disabled) return;

                            const parsed = parseDateTimeZoneValue(default_expression);
                            const tz = paramTimezones[name] || dateTimeZoneParts.tzValue || parsed.timezone || moment.tz.guess();

                            emitDefaultExpressionChange(originalIndex, `${dt.clone().milliseconds(0).format('YYYY-MM-DDTHH:mm:ss')} ${tz}`);
                          }}
                          mode="datetime"
                          placeholder="YYYY-MM-DD HH:mm:ss"
                          disabled={disabled}
                        />
                      </div>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={disabled}
                        onClick={() => openDetails(originalIndex)}
                        aria-label="parameter-details"
                        title="Details"
                        style={{ padding: '0px 8px', height: 32 }}
                      >
                        Output Format
                      </Button>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={disabled || !showClear}
                        onClick={() => emitDefaultExpressionChange(originalIndex, '')}
                      >
                        Clear
                      </Button>
                    </div>

                    <Form.Control
                      as="select"
                      disabled={disabled}
                      value={dateTimeZoneParts.tzValue}
                      onChange={(e) => {
                        if (disabled) return;
                        const tz = e.currentTarget.value;

                        setParamTimezones((prev) => ({ ...prev, [name]: tz }));

                        const parsed = parseDateTimeZoneValue(default_expression);

                        // ✅ Only write to default_expression if datetime part already exists
                        if (!parsed.datetime) return;

                        emitDefaultExpressionChange(originalIndex, `${parsed.datetime} ${tz}`);
                      }}
                    >
                      {moment.tz.names().map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </Form.Control>
                  </div>
                )}

                {isTime && (
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ flex: 1 }}>
                      <MaskedDateTimeField
                        value={timeMoment as any}
                        onChange={(dt) => {
                          if (disabled) return;
                          emitDefaultExpressionChange(originalIndex, dt.format('HH:mm:ss'));
                        }}
                        mode="time"
                        showCalendar={false}
                        placeholder="HH:mm:ss"
                        disabled={disabled}
                      />
                    </div>

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={disabled || !showClear}
                      onClick={() => emitDefaultExpressionChange(originalIndex, '')}
                    >
                      Clear
                    </Button>
                  </div>
                )}

                {!isDropdown && !isKeyValueDropdown && !isDate && !isDateTime && !isDateTimeZone && !isTime && (
                  <Form.Control
                    aria-label="default_expression"
                    disabled={disabled}
                    name="default_expression"
                    value={default_expression ?? ''}
                    onChange={(e) => onChange(e, originalIndex)}
                  />
                )}
              </Form.Group>
            </Col>

            <Col lg="1">
              <Form.Group>
                <Form.Label>Runtime</Form.Label>
                <Form.Check
                  aria-label="runtime"
                  disabled={disabled}
                  type="switch"
                  name="runtime"
                  checked={!!runtime}
                  value={String(runtime)}
                  onChange={(e) => onChange(e, originalIndex)}
                />
              </Form.Group>
            </Col>

            <Col lg="1">
              <Form.Group>
                <Form.Label>Mandatory</Form.Label>
                <Form.Check
                  aria-label="mandatory"
                  disabled={disabled}
                  type="switch"
                  name="mandatory"
                  checked={!!mandatory}
                  value={String(mandatory)}
                  onChange={(e) => onChange(e, originalIndex)}
                />
              </Form.Group>
            </Col>

            <Col lg="auto">
              <Button variant="secondary" style={{ marginTop: '23px' }} onClick={() => deleteParameter(name)}>
                Delete
              </Button>
            </Col>
          </Row>
        );
      })}

      <Row className="mt-2">
        <Col>
          <Button onClick={createNewParameter}>Add new parameter</Button>
        </Col>
      </Row>

      <Modal show={showDetailsModal} onHide={closeDetails} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Parameter details
            {detailsParameter?.name ? `: ${detailsParameter.name}` : ''}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Output format</Form.Label>
            <Form.Control
              aria-label="output_format"
              disabled={disabled || detailsParameterIndex === null}
              name="output_format"
              value={String(detailsParameter?.output_format ?? '')}
              placeholder="e.g. DD-MM-YYYY"
              onChange={(e) => {
                if (disabled) return;
                if (detailsParameterIndex === null) return;
                emitOutputFormatChange(detailsParameterIndex, e.target.value);
              }}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeDetails}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Parameters;
